import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { TextField } from "../../TextField";
import { Checkbox } from "../../Checkbox";
import { PartnerOnboardingService } from "../../../api/generated/services/PartnerOnboardingService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface AddPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (applicationId: string) => void;
}

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [cvr, setCvr] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [automaticallyInviteAdmin, setAutomaticallyInviteAdmin] = useState(false);
  const [automaticallyConfigureBilling, setAutomaticallyConfigureBilling] = useState(true);

  const resetForm = () => {
    setCompanyName("");
    setCvr("");
    setEmail("");
    setPhoneNumber("");
    setContactName("");
    setAutomaticallyInviteAdmin(false);
    setAutomaticallyConfigureBilling(false);
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const validateForm = (): string | null => {
    if (!companyName.trim()) {
      return "Company name is required";
    }
    if (!cvr.trim()) {
      return "CVR is required";
    }
    if (!email.trim()) {
      return "Email is required";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Please enter a valid email address";
    }
    if (!contactName.trim()) {
      return "Contact name is required";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await PartnerOnboardingService.postApiPartnerOnboardingSubmitApplicationFromCms({
        companyName: companyName.trim(),
        cvr: cvr.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim() || null,
        contactName: contactName.trim(),
        automaticallyInviteCompanyAdmin: automaticallyInviteAdmin,
        automaticallyConfigureBilling: automaticallyConfigureBilling,
        approved: true,
      });
      const applicationId = response.partnerApplicationId;
      resetForm();
      setLoading(false);
      onClose();
      if (applicationId) {
        onSuccess(applicationId);
      }
    } catch (err: any) {
      console.error("Error adding partner:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to add partner. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content md:max-w-[600px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Add Partner"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Add Partner</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            You are about to create a new partner. This will initiate the
            onboarding process for the company immediately.
          </p>
        </div>

        <TextField
          label="Company Name *"
          value={companyName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCompanyName(e.target.value)
          }
          placeholder="Enter company name"
          disabled={loading}
        />

        <TextField
          label="CVR *"
          value={cvr}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCvr(e.target.value)
          }
          placeholder="Enter CVR number"
          disabled={loading}
        />

        <TextField
          label="Email *"
          type="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          placeholder="Enter email address"
          disabled={loading}
        />

        <TextField
          label="Phone Number"
          type="tel"
          value={phoneNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPhoneNumber(e.target.value)
          }
          placeholder="Enter phone number (optional)"
          disabled={loading}
        />

        <TextField
          label="Contact Name *"
          value={contactName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setContactName(e.target.value)
          }
          placeholder="Enter contact name"
          disabled={loading}
        />

        <div className="py-2">
          <Checkbox
            checked={automaticallyInviteAdmin}
            onChange={(e) => setAutomaticallyInviteAdmin(e.target.checked)}
            label="Automatically invite company admin"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            If checked, an invitation email will be sent to the contact email
            address to set up their admin account.
          </p>
        </div>

        <div className="py-2">
          <Checkbox
            checked={automaticallyConfigureBilling}
            onChange={(e) => setAutomaticallyConfigureBilling(e.target.checked)}
            label="Automatically configure billing"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            If checked, billing will be configured for the company in Billwerk
            automatically.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading}>
            Add Partner
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddPartnerModal;
